import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Container,
  Chip,
  Button,
  Paper,
  CircularProgress,
  InputBase,
  Fade,
  Slide,
  Alert,
} from "@mui/material";
import { Helmet } from "react-helmet";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import StarIcon from "@mui/icons-material/Star";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../../../context/SocketContext";
import {
  fetchPublicMenu,
  fetchPublicOffers,
  selectPublicMenu,
  selectRestaurantInfo,
  selectMenuLoading,
  selectBogoOffers,
  selectCategoryOffers,
  selectItemOffers,
  selectCartOffers,
  selectCartOfferCalculation,
} from "../../../redux/slices/publicSlice";
// import { setRestaurant } from "../../../redux/slices/cartSlice";
import MenuItemCard from "../../../components/public/MenuItemCard";
import CartDrawer from "../../../components/public/CartDrawer";
import {
  fetchRestaurantDetails,
  selectRestaurantDetails,
  selectRestaurantDetailsLoading,
} from "../../../redux/slices/publicSlice";
import {
  fetchOverallStats,
  selectOverallStats,
} from "../../../redux/slices/statsSlice";
import {
  setRestaurant,
  selectCartItems,
  selectCartRestaurant,
} from "../../../redux/slices/cartSlice";

const VEG_MODE_KEY = "digital_manu_veg_only";

const PublicMenuPage = ({ restaurantId }) => {
  const dispatch = useDispatch();
  const categoryBarRef = useRef(null);
  const { socket, isConnected } = useSocket();

  const menu = useSelector(selectPublicMenu);
  const restaurantInfo = useSelector(selectRestaurantInfo);
  const loading = useSelector(selectMenuLoading);
  const bogoOffers = useSelector(selectBogoOffers);
  const categoryOffers = useSelector(selectCategoryOffers);
  const itemOffers = useSelector(selectItemOffers);
  const cartOffers = useSelector(selectCartOffers);
  const restaurantDetails = useSelector(selectRestaurantDetails);
  const restaurantDetailsLoading = useSelector(selectRestaurantDetailsLoading);
  const overallStats = useSelector(selectOverallStats);
  const [openCart, setOpenCart] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [vegOnly, setVegOnly] = useState(() => {
    try {
      const saved = localStorage.getItem(VEG_MODE_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  const [notification, setNotification] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const restaurant = restaurantDetails;

  const restaurantName = restaurantInfo?.restaurantName || "Restaurant";

  const restaurantSlug = restaurantInfo?.slug || "";

  const pageUrl = restaurantSlug
    ? `https://restaurantwala.in/restaurant/${restaurantSlug}/menu`
    : "https://restaurantwala.in/";

  const restaurantDescription =
    `${restaurantName} menu in ${restaurantInfo?.city || "India"}. ` +
    `Explore food items and offers, and order online directly through RestaurantWala.`;

  const restaurantImage =
    restaurantInfo?.profileImage?.url ||
    "https://restaurantwala.in/logo192.png";

  const restaurantSchema = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: restaurantName,
    url: pageUrl,
    description: restaurantDescription,
    image: restaurantImage,
    telephone: restaurantInfo?.mobile
      ? `+91${restaurantInfo.mobile}`
      : undefined,
    address: restaurantInfo?.restaurantAddress
      ? {
          "@type": "PostalAddress",
          streetAddress: restaurantInfo.restaurantAddress,
          addressLocality: restaurantInfo.city || undefined,
          addressRegion: restaurantInfo.state || undefined,
          addressCountry: "IN",
        }
      : undefined,
  };

  // 🔥 Cart from Redux selectors
  const cart = useSelector(selectCartItems);
  const cartRestaurant = useSelector(selectCartRestaurant);

  // 🔥 Filter only current restaurant's cart
  const filteredCart = useMemo(() => {
    if (!cartRestaurant || !restaurantInfo?._id) return [];
    return cartRestaurant === restaurantInfo._id.toString() ? cart : [];
  }, [cart, cartRestaurant, restaurantInfo]);

  const cartItemCount = useMemo(
    () =>
      filteredCart.reduce((t, i) => {
        if (i.isFree || i.price === 0 || i.itemName?.includes("(FREE)")) {
          return t;
        }
        return t + (i.qty || 1);
      }, 0),
    [filteredCart],
  );

  // 🔥 Cart calculation from Redux (backend authoritative)
  const cartCalculation = useSelector(selectCartOfferCalculation);

  // 🔥 View Cart total = backend ka finalTotal
  const cartTotal = useMemo(() => {
    if (cartCalculation?.finalTotal > 0) {
      return cartCalculation.finalTotal;
    }
    // Fallback
    return filteredCart.reduce((t, i) => {
      if (i.isFree || i.price === 0 || i.itemName?.includes("(FREE)")) {
        return t;
      }
      const finalPrice = i.discountedPrice || i.price || i.originalPrice || 0;
      return t + finalPrice * (i.qty || 1);
    }, 0);
  }, [cartCalculation?.finalTotal, filteredCart]);

  useEffect(() => {
    if (menu?.length > 0) {
      // console.log("Syncing menuItems from Redux, length:", menu.length);
      setMenuItems(menu);
    }
  }, [menu]);

  useEffect(() => {
    if (!socket || !isConnected || !restaurantId) {
      // console.log("⚠️ Cannot join public room - socket not ready");
      return;
    }
    socket.emit("public-join", { restaurantId });

    return () => {
      socket.emit("public-leave", { restaurantId });
      socket.off("public-join-confirm");
    };
  }, [socket, isConnected, restaurantId]);

  useEffect(() => {
    if (!socket || !isConnected || !restaurantId) {
      // console.log("⚠️ Socket or restaurantId not available");
      return;
    }

    const handleMenuUpdated = (data) => {
      const updatedItem = data.item;
      const itemId = updatedItem._id || updatedItem.id;
      setMenuItems((prev) => {
        if (!prev || !Array.isArray(prev)) {
          console.warn("⚠️ prev is not an array");
          return prev;
        }

        let itemUpdated = false;
        const updated = prev.map((category) => {
          const categoryCopy = { ...category };
          if ((category._id || category.id) === itemId) {
            itemUpdated = true;
            return {
              ...category,
              ...updatedItem,
              items: category.items ? [...category.items] : [],
            };
          }
          if (category.items && Array.isArray(category.items)) {
            const itemIndex = category.items.findIndex(
              (item) => (item._id || item.id) === itemId,
            );

            if (itemIndex !== -1) {
              itemUpdated = true;
              const oldItem = category.items[itemIndex];
              const newItem = {
                ...oldItem,
                ...updatedItem,
                _id: itemId,
                itemName: updatedItem.itemName || oldItem.itemName,
                basePrice:
                  updatedItem.basePrice !== undefined
                    ? updatedItem.basePrice
                    : updatedItem.price || oldItem.basePrice || oldItem.price,
                price:
                  updatedItem.price !== undefined
                    ? updatedItem.price
                    : updatedItem.basePrice ||
                      oldItem.price ||
                      oldItem.basePrice,
                isAvailable:
                  updatedItem.isAvailable !== undefined
                    ? updatedItem.isAvailable
                    : oldItem.isAvailable,
                description:
                  updatedItem.description !== undefined
                    ? updatedItem.description
                    : oldItem.description || "",
                image: updatedItem.image || oldItem.image || "",
                isVeg:
                  updatedItem.isVeg !== undefined
                    ? updatedItem.isVeg
                    : oldItem.isVeg,
                sizes: updatedItem.sizes || oldItem.sizes || [],
                variants: updatedItem.variants || oldItem.variants || [],
                addonGroups:
                  updatedItem.addonGroups || oldItem.addonGroups || [],
                stock:
                  updatedItem.stock !== undefined
                    ? updatedItem.stock
                    : oldItem.stock,
                createdAt: updatedItem.createdAt || oldItem.createdAt,
                updatedAt: updatedItem.updatedAt || oldItem.updatedAt,
              };

              const updatedItems = [...category.items];
              updatedItems[itemIndex] = newItem;
              return {
                ...category,
                items: updatedItems,
              };
            }
          }

          return category;
        });

        if (!itemUpdated) {
          dispatch(fetchPublicMenu(restaurantId));
          return prev;
        }
        return [...updated];
      });

      setNotification({
        message: `🍽️ ${updatedItem.itemName || "Menu item"} updated!`,
        severity: "info",
      });
      setTimeout(() => setNotification(null), 3000);
    };

    const handleMenuItemAdded = (data) => {
      const newItem = data.item;
      let categoryId = null;
      let categoryName = null;
      let categoryImage = null;
      if (newItem.category && typeof newItem.category === "object") {
        categoryId = newItem.category._id || newItem.category.id;
        categoryName = newItem.category.name;
        categoryImage = newItem.category.image || null;
      } else if (newItem.categoryId && typeof newItem.categoryId === "object") {
        categoryId = newItem.categoryId._id || newItem.categoryId.id;
        categoryName = newItem.categoryId.name;
        categoryImage = newItem.categoryId.image || null;
      } else if (newItem.categoryId && typeof newItem.categoryId === "string") {
        categoryId = newItem.categoryId;
        categoryName = newItem.categoryName || null;
      } else if (newItem.categoryName) {
        categoryId = newItem.categoryName;
        categoryName = newItem.categoryName;
      }
      setMenuItems((prev) => {
        if (!prev || !Array.isArray(prev)) return prev;
        let categoryIndex = prev.findIndex(
          (cat) => (cat.categoryId || cat._id) === categoryId,
        );

        if (categoryIndex === -1 && categoryName) {
          categoryIndex = prev.findIndex(
            (cat) =>
              cat.categoryName?.toLowerCase() === categoryName?.toLowerCase(),
          );
          if (categoryIndex !== -1) {
            const foundCategory = prev[categoryIndex];
            categoryId = foundCategory.categoryId || foundCategory._id;
          }
        }

        if (categoryIndex !== -1) {
          const updated = [...prev];
          const existingCategory = updated[categoryIndex];

          const finalCategoryName =
            categoryName || existingCategory.categoryName;
          const finalCategoryImage =
            categoryImage || existingCategory.categoryImage || null;

          const itemExists = existingCategory.items.some(
            (item) => (item._id || item.id) === (newItem._id || newItem.id),
          );

          if (!itemExists) {
            updated[categoryIndex] = {
              ...existingCategory,
              categoryName: finalCategoryName,
              categoryImage: finalCategoryImage,
              items: [
                ...(existingCategory.items || []),
                {
                  ...newItem,
                  categoryName: finalCategoryName,
                  categoryId: categoryId,
                },
              ],
            };
          } else {
            const itemIndex = existingCategory.items.findIndex(
              (item) => (item._id || item.id) === (newItem._id || newItem.id),
            );
            if (itemIndex !== -1) {
              const updatedItems = [...existingCategory.items];
              updatedItems[itemIndex] = {
                ...updatedItems[itemIndex],
                ...newItem,
                categoryName: finalCategoryName,
                categoryId: categoryId,
              };
              updated[categoryIndex] = {
                ...existingCategory,
                items: updatedItems,
              };
            }
          }
          return updated;
        } else {
          const finalCategoryName = categoryName || "New Category";
          const newCategory = {
            categoryId: categoryId || `temp_${Date.now()}`,
            categoryName: finalCategoryName,
            categoryImage: categoryImage || null,
            items: [
              {
                ...newItem,
                categoryName: finalCategoryName,
                categoryId: categoryId || `temp_${Date.now()}`,
              },
            ],
          };
          return [...prev, newCategory];
        }
      });

      setNotification({
        message: `🆕 New item: ${newItem.itemName} added to ${categoryName || "category"}!`,
        severity: "success",
      });
      setTimeout(() => setNotification(null), 3000);
    };

    const handleCategoryUpdated = (data) => {
      const categoryData = data.category;
      const categoryId = categoryData._id || categoryData.id;

      setMenuItems((prev) => {
        if (!prev || !Array.isArray(prev)) return prev;

        let categoryFound = false;

        const updated = prev.map((cat) => {
          if ((cat.categoryId || cat._id) === categoryId) {
            categoryFound = true;
            return {
              ...cat,
              categoryName: categoryData.name || cat.categoryName,
              categoryImage: categoryData.image || cat.categoryImage || null,
            };
          }
          return cat;
        });

        if (!categoryFound) {
          dispatch(fetchPublicMenu(restaurantId));
          return prev;
        }
        return updated;
      });
    };

    const handleCategoryAdded = (data) => {
      const categoryData = data.category;
      const categoryId = categoryData._id || categoryData.id;
      setMenuItems((prev) => {
        if (!prev || !Array.isArray(prev)) return prev;

        const categoryExists = prev.some(
          (cat) => (cat.categoryId || cat._id) === categoryId,
        );
        const newCategory = {
          categoryId: categoryId,
          categoryName: categoryData.name || "New Category",
          categoryImage: categoryData.image || null,
          items: categoryData.items || [],
        };
        return [...prev, newCategory];
      });
    };

    const handleMenuItemDeleted = (data) => {
      setMenuItems((prev) => {
        if (!prev || !Array.isArray(prev)) return prev;

        const updated = prev
          .map((category) => {
            if (category.items && Array.isArray(category.items)) {
              const filteredItems = category.items.filter(
                (item) => (item._id || item.id) !== data.itemId,
              );
              return { ...category, items: filteredItems };
            }
            return category;
          })
          .filter((category) => {
            return !(category.items && category.items.length === 0);
          });

        return updated;
      });

      setNotification({
        message: `${data.itemName || "Item"} removed from menu`,
        severity: "warning",
      });
      setTimeout(() => setNotification(null), 3000);
    };

    const handleStockUpdated = (data) => {
      setMenuItems((prev) => {
        if (!prev || !Array.isArray(prev)) return prev;

        return prev.map((category) => {
          if (category.items && Array.isArray(category.items)) {
            return {
              ...category,
              items: category.items.map((item) => {
                if ((item._id || item.id) === data.itemId) {
                  return {
                    ...item,
                    isAvailable: data.isAvailable,
                    stock: data.stock,
                  };
                }
                return item;
              }),
            };
          }
          return category;
        });
      });

      setNotification({
        message: data.isAvailable
          ? `${data.itemName} is back in stock!`
          : `${data.itemName} is out of stock!`,
        severity: data.isAvailable ? "success" : "error",
      });
      setTimeout(() => setNotification(null), 4000);
    };

    const handleItemOutOfStock = (data) => {
      setMenuItems((prev) => {
        if (!prev || !Array.isArray(prev)) return prev;

        return prev.map((category) => {
          if (category.items && Array.isArray(category.items)) {
            return {
              ...category,
              items: category.items.map((item) => {
                if ((item._id || item.id) === data.itemId) {
                  return { ...item, isAvailable: false, stock: 0 };
                }
                return item;
              }),
            };
          }
          return category;
        });
      });

      setNotification({
        message: `${data.itemName} is out of stock!`,
        severity: "error",
      });
      setTimeout(() => setNotification(null), 4000);
    };

    const handleNewOffer = (data) => {
      dispatch(fetchPublicOffers(restaurantId));

      setNotification({
        message: `🎉 New offer: ${data.offer?.title || "New offer"} available!`,
        severity: "success",
      });
      setTimeout(() => setNotification(null), 4000);
    };

    const handleOfferStatusChanged = (data) => {
      if (data.offer) {
        setNotification({
          message: data.offer.isActive
            ? `${data.offer.title} is now active!`
            : `${data.offer.title} has ended`,
          severity: data.offer.isActive ? "success" : "warning",
        });
        setTimeout(() => setNotification(null), 3000);
      }
    };

    const handleMenuItemToggled = (data) => {
      if (data.itemId && data.isAvailable !== undefined) {
        setMenuItems((prev) => {
          if (!prev || !Array.isArray(prev)) return prev;

          return prev.map((category) => {
            if (category.items && Array.isArray(category.items)) {
              return {
                ...category,
                items: category.items.map((item) => {
                  if ((item._id || item.id) === data.itemId) {
                    return { ...item, isAvailable: data.isAvailable };
                  }
                  return item;
                }),
              };
            }
            return category;
          });
        });

        setNotification({
          message: data.isAvailable
            ? `${data.itemName} is now available!`
            : `${data.itemName} is now unavailable`,
          severity: data.isAvailable ? "success" : "warning",
        });
        setTimeout(() => setNotification(null), 3000);
      }
    };

    socket.on("menu-updated", handleMenuUpdated);
    socket.on("menu-item-added", handleMenuItemAdded);
    socket.on("menu-item-deleted", handleMenuItemDeleted);
    socket.on("menu-stock-updated", handleStockUpdated);
    socket.on("item-out-of-stock", handleItemOutOfStock);
    socket.on("new-offer", handleNewOffer);
    socket.on("offer-status-changed", handleOfferStatusChanged);
    socket.on("menu-item-toggled", handleMenuItemToggled);
    socket.on("category-updated", handleCategoryUpdated);
    socket.on("category-added", handleCategoryAdded);

    return () => {
      socket.off("menu-updated", handleMenuUpdated);
      socket.off("menu-item-added", handleMenuItemAdded);
      socket.off("menu-item-deleted", handleMenuItemDeleted);
      socket.off("menu-stock-updated", handleStockUpdated);
      socket.off("item-out-of-stock", handleItemOutOfStock);
      socket.off("new-offer", handleNewOffer);
      socket.off("offer-status-changed", handleOfferStatusChanged);
      socket.off("menu-item-toggled", handleMenuItemToggled);
      socket.off("category-updated", handleCategoryUpdated);
      socket.off("category-added", handleCategoryAdded);
    };
  }, [socket, isConnected, restaurantId, dispatch]);

  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchRestaurantDetails(restaurantId));
      dispatch(fetchPublicMenu(restaurantId));
      dispatch(fetchPublicOffers(restaurantId));
      dispatch(fetchOverallStats(restaurantId));
    }
  }, [dispatch, restaurantId]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (restaurantInfo?._id) dispatch(setRestaurant(restaurantInfo._id));
  }, [restaurantInfo, dispatch]);

  useEffect(() => {
    if (menu?.length > 0) {
      setActiveCategory(menu[0].categoryId);
      setMenuItems(menu);
    }
  }, [menu]);

  useEffect(() => {
    const handleOpenCartEvent = () => {
      setOpenCart(true); // ya jo bhi cart open karne ka state setter ho
    };
    window.addEventListener("open-cart-drawer", handleOpenCartEvent);
    return () => {
      window.removeEventListener("open-cart-drawer", handleOpenCartEvent);
    };
  }, []);

  const getCategoryOffer = (categoryId) => {
    if (!categoryId) return null;
    const offers = categoryOffers[categoryId.toString()] || [];
    return offers[0] || null;
  };

  const getItemOffer = (itemId) => {
    if (!itemId) return null;
    const offers = itemOffers[itemId.toString()] || [];
    return offers[0] || null;
  };

  const isItemInBogoOffer = (itemId, categoryId) => {
    if (!bogoOffers?.length) return false;
    return bogoOffers.some((offer) => {
      if (offer.scope === "ITEM" && offer.menuItem) {
        return (
          (offer.menuItem._id || offer.menuItem).toString() ===
          itemId?.toString()
        );
      }
      if (offer.scope === "CATEGORY" && offer.categoryId) {
        return (
          (offer.categoryId._id || offer.categoryId).toString() ===
          categoryId?.toString()
        );
      }
      if (offer.scope === "CART") return true;
      return false;
    });
  };

  const categories = useMemo(() => {
    const baseCategories = [
      { id: "all", name: "All", icon: "🍽️", image: null, hasOffer: false },
      ...(menuItems || []).map((cat) => ({
        id: cat.categoryId,
        name: cat.categoryName || "Unnamed Category",
        image: cat.categoryImage || null,
        icon: null,
        count: cat.items?.length || 0,
        hasOffer: !!getCategoryOffer(cat.categoryId),
      })),
    ];
    return baseCategories;
  }, [menuItems, categoryOffers]);

  const filteredItems = useMemo(() => {
    let items =
      activeCategory === "all"
        ? (menuItems || []).flatMap((cat) =>
            (cat.items || []).map((item) => ({
              ...item,
              categoryName: cat.categoryName,
              categoryId: cat.categoryId,
            })),
          )
        : (
            (menuItems || []).find((cat) => cat.categoryId === activeCategory)
              ?.items || []
          ).map((item) => ({
            ...item,
            categoryName:
              (menuItems || []).find((cat) => cat.categoryId === activeCategory)
                ?.categoryName || "",
            categoryId: activeCategory,
          }));

    if (searchQuery.trim())
      items = items.filter(
        (i) =>
          i.itemName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          i.description?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    if (vegOnly) items = items.filter((i) => i.isVeg);

    const result = items.map((item) => ({
      ...item,
      itemOffer: getItemOffer(item._id),
      categoryOffer: getCategoryOffer(item.categoryId),
      isBogoEligible: isItemInBogoOffer(item._id, item.categoryId),
      isAvailable: item.isAvailable !== false,
    }));

    return result;
  }, [
    menuItems,
    activeCategory,
    searchQuery,
    vegOnly,
    categoryOffers,
    itemOffers,
    bogoOffers,
  ]);

  const scrollCategoryIntoView = (catId) => {
    setActiveCategory(catId);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      const chip = categoryBarRef.current?.querySelector(
        `[data-cat-id="${catId}"]`,
      );
      chip?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }, 50);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: "#2e7d32" }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1b5e20" }}>
          Loading Menu...
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Preparing delicious items 🍴
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", pb: 12 }}>
      <Helmet>
        <title>{`${restaurantName} Menu | Order Online | RestaurantWala`}</title>

        <meta
          name="description"
          content={restaurantDescription.slice(0, 160)}
        />

        <link rel="canonical" href={pageUrl} />

        <meta
          property="og:title"
          content={`${restaurantName} Menu | RestaurantWala`}
        />
        <meta
          property="og:description"
          content={restaurantDescription.slice(0, 160)}
        />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={restaurantImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${restaurantName} Menu | RestaurantWala`}
        />
        <meta
          name="twitter:description"
          content={restaurantDescription.slice(0, 160)}
        />
        <meta name="twitter:image" content={restaurantImage} />

        <script type="application/ld+json">
          {JSON.stringify(restaurantSchema)}
        </script>
      </Helmet>

      {/* AppBar */}
      <AppBar
        position="sticky"
        elevation={scrolled ? 3 : 0}
        sx={{
          bgcolor: "white",
          color: "#1a1a1a",
          borderBottom: "1px solid #ebebeb",
          transition: "box-shadow 0.2s",
        }}
      >
        <Toolbar sx={{ px: 1.5, minHeight: "56px !important", gap: 1 }}>
          {searchOpen ? (
            <Fade in={searchOpen}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <SearchIcon sx={{ color: "#999", mr: 1, fontSize: 20 }} />
                <InputBase
                  autoFocus
                  placeholder="Search dishes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ flex: 1, fontSize: "0.95rem" }}
                />
                {searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery("")}
                    sx={{ p: 0 }}
                  >
                    <CloseIcon sx={{ fontSize: 18, color: "#999" }} />
                  </IconButton>
                )}
              </Box>
            </Fade>
          ) : (
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {restaurantInfo?.restaurantName || "Menu"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 12, color: "#666" }} />
                <Typography variant="caption" color="text.secondary">
                  {`${restaurant?.deliveryTime?.min || 25}-${restaurant?.deliveryTime?.max || 35} min`}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mx: 0.3 }}
                >
                  •
                </Typography>
                <StarIcon sx={{ fontSize: 12, color: "#f59e0b" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#f59e0b", fontWeight: 600 }}
                >
                  {overallStats?.averageRating || 0}
                </Typography>
              </Box>
            </Box>
          )}
          <IconButton
            size="small"
            onClick={() => {
              setSearchOpen(!searchOpen);
              if (searchOpen) setSearchQuery("");
            }}
            sx={{ color: "#1a1a1a", background: "#fff" }}
          >
            {searchOpen ? <CloseIcon /> : <SearchIcon />}
          </IconButton>
          {/* <IconButton
            size="small"
            onClick={() => setOpenCart(true)}
            sx={{ color: "#1a1a1a" }}
          >
            <Badge badgeContent={cartItemCount} color="error">
              <ShoppingCartIcon />
            </Badge>
          </IconButton> */}
        </Toolbar>

        {cartOffers?.length > 0 && (
          <Box
            sx={{
              bgcolor: "#1b5e20",
              py: 0.6,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
            }}
          >
            <LocalOfferIcon sx={{ fontSize: 13, color: "#a5d6a7" }} />
            <Typography
              variant="caption"
              sx={{ color: "#e8f5e9", fontWeight: 500, fontSize: "0.72rem" }}
            >
              Use code{" "}
              <Box
                component="span"
                sx={{
                  bgcolor: "#a5d6a7",
                  color: "#1b5e20",
                  px: 0.6,
                  borderRadius: 0.5,
                  fontWeight: 700,
                }}
              >
                {cartOffers[0]?.offerCode}
              </Box>{" "}
              ·{" "}
              {cartOffers[0]?.description
                ? cartOffers[0]?.description
                : cartOffers[0]?.discountType === "PERCENT"
                  ? `${cartOffers[0]?.value}% off`
                  : cartOffers[0]?.discountType === "FLAT"
                    ? `₹${cartOffers[0]?.value} off`
                    : "Limited time offer"}
            </Typography>
          </Box>
        )}

        <Box
          ref={categoryBarRef}
          sx={{
            display: "flex",
            overflowX: "auto",
            px: 1.5,
            py: 1,
            gap: 0.8,
            bgcolor: "white",
            borderTop: "1px solid #f0f0f0",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Chip
            label="🥗 Veg"
            size="small"
            onClick={() => {
              const value = !vegOnly;
              setVegOnly(value);
              localStorage.setItem(VEG_MODE_KEY, JSON.stringify(value));
            }}
            sx={{
              flexShrink: 0,
              height: 32,
              bgcolor: vegOnly ? "#2e7d32" : "#f5f5f5",
              color: vegOnly ? "white" : "#555",
              fontWeight: 600,
              fontSize: "0.75rem",
              border: vegOnly ? "none" : "1px solid #e0e0e0",
            }}
          />
          {categories?.map((cat) => (
            <Box key={cat.id} sx={{ position: "relative", flexShrink: 0 }}>
              <Chip
                data-cat-id={cat.id}
                size="small"
                onClick={() => scrollCategoryIntoView(cat.id)}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    {cat.image ? (
                      <Box
                        component="img"
                        src={cat.image}
                        alt={cat.name}
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        component="span"
                        sx={{ fontSize: "1rem", lineHeight: 1 }}
                      >
                        {cat.icon}
                      </Box>
                    )}
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: activeCategory === cat.id ? 700 : 500,
                      }}
                    >
                      {cat.name}
                    </Typography>
                  </Box>
                }
                sx={{
                  height: 34,
                  bgcolor: activeCategory === cat.id ? "#e8f5e9" : "#f5f5f5",
                  color: activeCategory === cat.id ? "#2e7d32" : "#555",
                  border:
                    activeCategory === cat.id
                      ? "1.5px solid #2e7d32"
                      : "1px solid transparent",
                  transition: "all 0.15s",
                  "& .MuiChip-label": { px: 1.2 },
                }}
              />
              {cat.hasOffer && cat.id !== "all" && (
                <Chip
                  label="OFFER"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -5,
                    right: -5,
                    height: 16,
                    fontSize: "0.55rem",
                    bgcolor: "#ff9800",
                    color: "white",
                    fontWeight: 700,
                    "& .MuiChip-label": { px: 0.6 },
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </AppBar>

      {/* Menu Items */}
      <Container
        disableGutters
        sx={{ maxWidth: "600px !important", mx: "auto", px: 1.5, py: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}
            >
              {activeCategory === "all"
                ? "All Items"
                : categories.find((c) => c.id === activeCategory)?.name ||
                  "Menu"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filteredItems.length} items{vegOnly ? " · Veg only" : ""}
              {searchQuery ? ` · "${searchQuery}"` : ""}
            </Typography>
          </Box>
          {(searchQuery || vegOnly) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSearchQuery("");
                const value = false;
                setVegOnly(value);
                localStorage.setItem(VEG_MODE_KEY, JSON.stringify(value));
              }}
              sx={{
                fontSize: "0.7rem",
                height: 28,
                borderRadius: 2,
                textTransform: "none",
                borderColor: "#ccc",
                color: "#666",
              }}
            >
              Clear
            </Button>
          )}
        </Box>

        {filteredItems.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {filteredItems.map((item) => (
              <Box
                key={`${item._id}-${item.itemName}`}
                sx={{
                  bgcolor: "white",
                  borderRadius: 2.5,
                  overflow: "hidden",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                  border: "1px solid #f0f0f0",
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: "0 3px 12px rgba(0,0,0,0.12)" },
                }}
              >
                <MenuItemCard
                  item={item}
                  categoryName={item.categoryName}
                  itemOffer={item.itemOffer}
                  categoryOffer={item.categoryOffer}
                  isBogoEligible={item.isBogoEligible}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: "white",
              borderRadius: 3,
              border: "1px solid #f0f0f0",
            }}
          >
            <Typography sx={{ fontSize: "2.5rem", mb: 1 }}>🔍</Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "#333" }}
            >
              {searchQuery
                ? `No results for "${searchQuery}"`
                : "No items here"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Try a different category
            </Typography>
          </Box>
        )}

        {activeCategory !== "all" && (menuItems || []).length > 1 && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: "#1a1a1a",
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              Browse All Categories{" "}
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#888" }} />
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}
            >
              {(menuItems || []).map((category) => {
                const catOffer = getCategoryOffer(category.categoryId);
                return (
                  <Paper
                    key={category.categoryId}
                    elevation={0}
                    onClick={() => scrollCategoryIntoView(category.categoryId)}
                    sx={{
                      p: 1.5,
                      bgcolor: "white",
                      borderRadius: 2.5,
                      border: "1px solid #f0f0f0",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      transition: "all 0.15s",
                      "&:hover": {
                        borderColor: "#2e7d32",
                        bgcolor: "#f9fff9",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(46,125,50,0.12)",
                      },
                    }}
                  >
                    {category.categoryImage ? (
                      <Box
                        component="img"
                        src={category.categoryImage}
                        alt={category.categoryName}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          bgcolor: "#e8f5e9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontSize: "1.3rem",
                          fontWeight: 700,
                          color: "#2e7d32",
                        }}
                      >
                        {category.categoryName?.charAt(0)}
                      </Box>
                    )}
                    <Box sx={{ overflow: "hidden", flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.82rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {category.categoryName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#888", fontSize: "0.72rem" }}
                      >
                        {category.items?.length || 0} items
                      </Typography>
                      {catOffer && (
                        <Chip
                          label={
                            catOffer.offerCategory === "DISCOUNT"
                              ? `${catOffer.value}${catOffer.discountType === "PERCENT" ? "%" : "₹"} OFF`
                              : "BOGO"
                          }
                          size="small"
                          sx={{
                            mt: 0.5,
                            height: 16,
                            fontSize: "0.55rem",
                            bgcolor: "#ff9800",
                            color: "white",
                            fontWeight: 700,
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}
      </Container>

      {cartItemCount > 0 && (
        <Slide direction="up" in>
          <Box
            sx={{
              position: "fixed",
              bottom: 56,
              left: 0,
              right: 0,
              px: 2,
              pb: 1.5,
              pt: 1,
              background:
                "linear-gradient(to top,rgba(255,255,255,1) 70%,rgba(255,255,255,0))",
              zIndex: 100,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => setOpenCart(true)}
              sx={{
                borderRadius: 2.5,
                bgcolor: "#2e7d32",
                "&:hover": { bgcolor: "#1b5e20" },
                py: 1.6,
                px: 2.5,
                boxShadow: "0 4px 20px rgba(46,125,50,0.4)",
                display: "flex",
                justifyContent: "space-between",
                textTransform: "none",
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.3,
                  minWidth: 28,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "white", fontWeight: 700, fontSize: "0.8rem" }}
                >
                  {cartItemCount}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, color: "white", fontSize: "0.95rem" }}
              >
                View Cart
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "white" }}
              >
                ₹{cartTotal.toFixed(0)}
              </Typography>
            </Button>
          </Box>
        </Slide>
      )}

      <CartDrawer
        open={openCart}
        onClose={() => setOpenCart(false)}
        restaurantId={restaurantId}
        restaurantInfo={restaurantInfo}
      />

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
          }
        `}
      </style>
    </Box>
  );
};

export default PublicMenuPage;
